import React, { useState, useEffect } from 'react';
import { getProfile, updateProfile } from '../api/educentialApi';
import {
  User,
  Mail,
  Calendar,
  Shield,
  Save,
  Edit2,
  X,
  CheckCircle,
  Phone,
  MapPin,
  Briefcase,
  AlertTriangle
} from 'lucide-react';

export default function Profile() {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');

  // Extended profile data
  const [profileData, setProfileData] = useState({
    email: '',
    fullName: '',
    phone: '',
    country: '',
    city: '',
    occupation: '',
    bio: '',
    role: '',
    createdAt: ''
  });

  const [originalData, setOriginalData] = useState({});

  // Load profile data on mount
  useEffect(() => {
    const loadProfile = async () => {
      setIsLoading(true);
      setError('');
      try {
        const profile = await getProfile();
        const data = {
          email: profile.email || '',
          fullName: profile.fullName || '',
          phone: profile.phone || '',
          country: profile.country || '',
          city: profile.city || '',
          occupation: profile.occupation || '',
          bio: profile.bio || '',
          role: profile.role || '',
          createdAt: profile.createdAt || ''
        };
        setProfileData(data);
        setOriginalData(data);
      } catch (err) {
        console.error('Failed to load profile:', err);
        setError('Failed to load profile data');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    try {
      await updateProfile(profileData);
      setOriginalData(profileData);
      setSaveSuccess(true);
      setIsEditing(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to update profile:', error);
      setError(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setProfileData(originalData);
    setError('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="bg-gray-50 min-h-full flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
              <p className="text-gray-500 text-sm mt-1">
                Manage your personal information
              </p>
            </div>
            {saveSuccess && (
              <div className="flex items-center space-x-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-700">Profile updated!</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
              <div className="text-center">
                <div className="w-24 h-24 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                  <User className="w-12 h-12 text-yellow-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">
                  {profileData.fullName || (profileData.role === 'EDUCATOR' ? 'Educator' : 'Student')}
                </h2>
                <p className="text-sm text-gray-500 mb-4">{profileData.email}</p>

                <div className="flex items-center justify-center space-x-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-full inline-flex">
                  <Shield className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-semibold text-gray-700 capitalize">
                    {profileData.role ? profileData.role.toLowerCase() : 'student'}
                  </span>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>Member since {formatDate(profileData.createdAt)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                    <span>Edit</span>
                  </button>
                ) : (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleCancel}
                      className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-white bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-400 rounded-lg transition-colors"
                    >
                      {isSaving ? (
                        <>
                          <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>Save</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div className="p-6 space-y-6">
                {/* Error Message */}
                {error && (
                  <div className="flex items-center space-x-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <span className="text-sm text-red-700">{error}</span>
                  </div>
                )}

                {/* Full Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profileData.fullName}
                      onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 focus:bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 rounded-lg transition-colors text-sm"
                      placeholder="Enter your full name"
                    />
                  ) : (
                    <div className="flex items-center space-x-3 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {profileData.fullName || 'Not set'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="flex items-center space-x-3 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{profileData.email}</span>
                    <span className="ml-auto text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                      Verified
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Email cannot be changed. Contact support if needed.
                  </p>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 focus:bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 rounded-lg transition-colors text-sm"
                      placeholder="+1 (555) 000-0000"
                    />
                  ) : (
                    <div className="flex items-center space-x-3 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {profileData.phone || 'Not set'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Location */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Country
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={profileData.country}
                        onChange={(e) => setProfileData({ ...profileData, country: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 focus:bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 rounded-lg transition-colors text-sm"
                        placeholder="e.g., United States"
                      />
                    ) : (
                      <div className="flex items-center space-x-3 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {profileData.country || 'Not set'}
                        </span>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      City
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={profileData.city}
                        onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 focus:bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 rounded-lg transition-colors text-sm"
                        placeholder="e.g., New York"
                      />
                    ) : (
                      <div className="flex items-center space-x-3 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                        <span className="text-sm text-gray-900">
                          {profileData.city || 'Not set'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Occupation */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Occupation
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profileData.occupation}
                      onChange={(e) => setProfileData({ ...profileData, occupation: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 focus:bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 rounded-lg transition-colors text-sm"
                      placeholder="e.g., Software Engineer"
                    />
                  ) : (
                    <div className="flex items-center space-x-3 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                      <Briefcase className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {profileData.occupation || 'Not set'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Bio
                  </label>
                  {isEditing ? (
                    <textarea
                      value={profileData.bio}
                      onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-2.5 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 focus:bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 rounded-lg transition-colors text-sm resize-none"
                      placeholder="Tell us about yourself..."
                    />
                  ) : (
                    <div className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg min-h-[100px]">
                      <p className="text-sm text-gray-900">
                        {profileData.bio || 'Not set'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
